import {
    Box,
    Checkbox,
    IconButton,
    Link,
    Modal,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { useApi } from '../api/Api';
import { useRequest, RequestSnackbar } from '../api/Request';
import { Question } from '../database/theSource';
import { ArtistInfo, User } from '../database/user';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import Icon from '../components/Icon';
import PaymentInfoModal from './PaymentInfoModal';
import { PrivacyStatus } from './constants/PrivacyStatus';
import { privacyPolicyUrl, termsOfUseUrl } from '../components/LegalBar';
import { useState } from 'react';
import { motion } from 'framer-motion';
import CostCard from './CostCard';
import { ResponseType } from './constants/ResponseType';
import { PaymentModel } from './constants/PaymentModel';
import { CreateQuestionRequest, SetUpFuturePaymentsRequest } from '../api/theSourceApi';

interface QuestionSubmissionModalProps {
    isModalOpen: boolean;
    handleCloseModal: () => void;
    onSubmit: (question: Question) => void;
    user: User;
    questionContent: string;
    responseType: string;
    artistInfo: ArtistInfo;
    isAcknowledged: boolean;
    isPrivate: string;
    setIsAcknowledged: (value: boolean) => void;
    selectedCost: number;
    setSelectedCost: (cost: number) => void;
}

const QuestionSubmissionModal: React.FC<QuestionSubmissionModalProps> = ({
    isModalOpen,
    handleCloseModal,
    onSubmit,
    user,
    questionContent,
    responseType,
    artistInfo,
    isAcknowledged,
    isPrivate,
    setIsAcknowledged,
    selectedCost,
    setSelectedCost,
}) => {
    const createQuestionRequest = useRequest<Question>();
    const setUpFuturePaymentsRequest = useRequest();
    const api = useApi();
    const theme = useTheme();

    const createQuestion = (
        content: string,
        type: string,
        isPrivate: string,
        artistInfo: ArtistInfo
    ) => {
        const payload: CreateQuestionRequest = {
            content: content,
            responseType: type,
            isAcknowledged,
            isPrivate: isPrivate,
        };
        // Add selectedPrice only if the payment model is PWYW
        if (artistInfo.paymentModel === PaymentModel.PWYW) {
            payload.selectedPrice = selectedCost;
        }

        createQuestionRequest.onStart();
        api.createQuestion(payload)
            .then((resp) => {
                onSubmit(resp.data);
                handleCloseModal();
                createQuestionRequest.onSuccess();
            })
            .catch((err: unknown) => {
                console.error('createQuestion error: ', err);
                createQuestionRequest.onFailure(err);
            });
    };

    const isPaymentInfoComplete = () => {
        return !(
            user.paymentInfo === null ||
            user.paymentInfo?.customerId === '' ||
            user.paymentInfo?.paymentMethodId === ''
        );
    };

    const handleAcknowledgement = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsAcknowledged(event.target.checked);
    };

    const setUpFuturePayment = (content: string, type: string, isPrivate: string) => {
        const payload: SetUpFuturePaymentsRequest = {
            content: content,
            responseType: type,
            isAcknowledged,
            isPrivate: isPrivate,
        };
        // Add selectedPrice only if the payment model is PWYW
        if (artistInfo.paymentModel === PaymentModel.PWYW) {
            payload.selectedPrice = selectedCost;
        }
        setUpFuturePaymentsRequest.onStart();
        api.setUpFuturePayment(payload)
            .then((resp) => {
                window.location.href = resp.data.url;
            })
            .catch((err: unknown) => {
                console.error('setUpFuturePayment: ', err);
                setUpFuturePaymentsRequest.onFailure(err);
            });
    };

    return (
        <Modal
            open={isModalOpen}
            onClose={handleCloseModal}
            slotProps={{
                backdrop: {
                    sx: {
                        backdropFilter: 'blur(4px)',
                        backgroundColor: 'rgba(0, 0, 0, 0.65)',
                    },
                },
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '90%', md: '50%' },
                    outline: 'none',
                }}
            >
            <motion.div
                initial={{ opacity: 0, y: 48 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 28 }}
            >
            <Stack
                sx={{
                    borderRadius: '16px',
                    border: `1px solid ${theme.palette.primary.main}`,
                    maxHeight: '80%',
                    backgroundColor: `${theme.palette.background.default}`,
                    boxShadow: '0 24px 64px rgba(0,0,0,0.85), 0 0 0 1px rgba(139, 0, 0, 0.12)',
                    padding: 2,
                }}
            >
                <Stack direction='row' alignItems='center' justifyContent='space-between'>
                    <Typography variant='h6' sx={{ fontFamily: 'LemonMilk' }}>
                        Request Confirmation
                    </Typography>
                    <IconButton
                        onClick={handleCloseModal}
                        sx={{
                            marginLeft: 'auto',
                            color: `${theme.palette.secondary.main}`,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
                <Stack padding={2} spacing={2}>
                    <Stack>
                        <Stack direction='row' alignItems='center' spacing={1}>
                            <Typography variant='subtitle1' fontFamily='LemonMilk'>
                                Your Request
                            </Typography>
                            <Icon fontSize='inherit' name='question' color='primary' />
                        </Stack>
                        <Typography
                            variant='body1'
                            color='primary'
                            sx={{ wordWrap: 'break-word' }}
                        >
                            {questionContent}
                        </Typography>
                    </Stack>
                    <Stack
                        direction='row'
                        justifyContent='flex-start'
                        flexWrap='wrap'
                        rowGap={2}
                    >
                        <Stack paddingRight={3}>
                            <Stack direction='row' alignItems='center' spacing={1}>
                                <Typography variant='subtitle1' fontFamily='LemonMilk'>
                                    Type
                                </Typography>
                                <Icon
                                    fontSize='inherit'
                                    name='responseTypes'
                                    color='primary'
                                />
                            </Stack>
                            <Typography variant='body1' color='primary'>
                                {responseType.charAt(0).toUpperCase() +
                                    responseType.slice(1).toLowerCase()}
                            </Typography>
                        </Stack>
                        <Stack paddingRight={3}>
                            <Stack direction='row' alignItems='center' spacing={1}>
                                <Typography variant='subtitle1' fontFamily='LemonMilk'>
                                    Privacy
                                </Typography>
                                <Icon
                                    fontSize='inherit'
                                    name={PrivacyStatus.Private}
                                    color='primary'
                                />
                            </Stack>
                            <Typography variant='body1' color='primary'>
                                {isPrivate === 'false'
                                    ? PrivacyStatus.Public
                                    : PrivacyStatus.Private}
                            </Typography>
                        </Stack>
                        <CostCard
                            artistInfo={artistInfo}
                            responseType={responseType as ResponseType}
                            onCostSelect={(cost: number) => setSelectedCost(cost)}
                            selectedCost={selectedCost}
                        />
                    </Stack>
                    <Stack direction='row' alignItems='center'>
                        <Checkbox
                            checked={isAcknowledged}
                            onChange={handleAcknowledgement}
                            sx={{
                                color: theme.palette.secondary.main,
                            }}
                        />
                        <Typography sx={{ fontSize: '0.75rem' }}>
                            I acknowledge I will not be charged unless my request is
                            answered within 30 days and I accept the{' '}
                            {
                                <Link
                                    component='button'
                                    onClick={() => {
                                        window.open(termsOfUseUrl, '_blank');
                                    }}
                                >
                                    Terms of Use
                                </Link>
                            }{' '}
                            and{' '}
                            {
                                <Link
                                    component='button'
                                    onClick={() => {
                                        window.open(privacyPolicyUrl, '_blank');
                                    }}
                                >
                                    Privacy Policy
                                </Link>
                            }
                            .
                        </Typography>
                    </Stack>
                    <PaymentInfoModal
                        user={user}
                        choosePaymentMethod={() =>
                            setUpFuturePayment(questionContent, responseType, isPrivate)
                        }
                    />
                </Stack>
                <LoadingButton
                    variant='contained'
                    onClick={() =>
                        createQuestion(
                            questionContent,
                            responseType,
                            isPrivate,
                            artistInfo
                        )
                    }
                    loading={createQuestionRequest.isLoading()}
                    sx={{
                        opacity: isPaymentInfoComplete() && isAcknowledged ? 1 : 0.4,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        fontSize: '18px',
                        padding: '12px 16px',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
                    }}
                    disabled={!isPaymentInfoComplete() || !isAcknowledged}
                >
                    Buy Request for ${selectedCost}
                </LoadingButton>
                <RequestSnackbar
                    request={createQuestionRequest}
                    showSuccess={true}
                    defaultSuccessMessage='Request submitted successfully!'
                />
            </Stack>
            </motion.div>
            </Box>
        </Modal>
    );
};

export default QuestionSubmissionModal;
